/**
 * Partner API client.
 *
 * Money crosses the wire as a decimal STRING of micros, never a JSON number,
 * for the same reason as the dashboard: ₹9,000 crore in micros exceeds
 * Number.MAX_SAFE_INTEGER and JSON.parse would silently round it.
 *
 * The session token is also kept in localStorage alongside the httpOnly
 * cookie. That is not belt-and-braces for its own sake: iOS Safari drops
 * cookies on a site added to the home screen in some configurations, and a
 * printer who has to sign in again every morning stops using the app.
 */
const BASE = import.meta.env.VITE_API_BASE ?? '/api';
const TOKEN_KEY = 'lulu_partner_token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function storedToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = storedToken();

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
    credentials: 'include',
  });

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => ({}))) as {
    error?: { code: string; message: string };
  };

  if (!res.ok) {
    // A 401 means the session is gone. Clearing it here rather than at each
    // call site is what stops the app looping on a dead token.
    if (res.status === 401) storeToken(null);
    throw new ApiError(
      res.status,
      body.error?.code ?? 'unknown',
      body.error?.message ?? 'Something went wrong.',
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(payload ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** PUT with a JSON body (creator profile, channels, packages). */
  putJson: <T>(path: string, payload: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(payload ?? {}) }),
  /** A file as raw bytes (a photo). */
  put: <T>(path: string, file: Blob) =>
    request<T>(path, {
      method: 'PUT',
      body: file,
      headers: { 'content-type': 'application/octet-stream' },
    }),
};

/** A picture the API serves, fetched with the session (for <img src>). */
export async function fetchPicture(path: string): Promise<string | null> {
  const token = storedToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

// ------------------------------------------------------------------- types

export interface VendorProfile {
  id: string;
  businessName: string;
  contactName: string;
  phone: string;
  capabilities: string[];
  /** Prints / mounts its own sites (it gets those jobs). */
  printsOwnSites?: boolean;
  baseCity: string;
  baseState: string;
  status: string;
  jobsCompleted: number;
  jobsLate: number;
  qualityScore: number | null;
  commissionPct: number;
  paymentTermsDays: number;
  languages: string[];
  hasGstin: boolean;
  hasPan: boolean;
  verifiedAt: string | null;
}

export interface MeResponse {
  registered: boolean;
  phone: string;
  vendor: VendorProfile | null;
  /** This login: the owner, or a team login (staff). */
  login?: { name: string | null; role: string; photoAt: string | null };
}

export interface Invitation {
  quoteId: string;
  jobId: string;
  title: string;
  city: string;
  capability: string;
  neededBy: string;
  closesAt: string | null;
  spec: Record<string, unknown> | null;
  deliveryAddress: string | null;
  budgetHintMicros: string | null;
  quoted: boolean;
  quotedAmountMicros: string | null;
  quotedLeadTimeDays: number | null;
  proofRequired: string;
}

export interface ActiveJob {
  jobId: string;
  title: string;
  city: string;
  capability: string;
  neededBy: string;
  status: string;
  awardedMicros: string | null;
  deliveryAddress: string | null;
  spec: Record<string, unknown> | null;
  proofCount: number;
  proofRequired: string;
}

export interface JobsResponse {
  invitations: Invitation[];
  active: ActiveJob[];
}

export interface Payout {
  id: string;
  jobTitle: string | null;
  grossMicros: string;
  commissionMicros: string;
  tdsMicros: string;
  netMicros: string;
  status: string;
  dueOn: string | null;
  paidAt: string | null;
  reference: string | null;
  holdReason: string | null;
}

export interface PayoutsResponse {
  pendingNetMicros: string;
  payouts: Payout[];
}

export interface Listing {
  id: string;
  title: string;
  mediaType: string;
  city: string;
  rateMicros: string;
  rateUnit: string;
  status: string;
  estDailyImpressions: number | null;
  impressionMethod: string | null;
  bookedDays: number;
}

export interface ListingsResponse {
  listings: Listing[];
}
